import React, { ChangeEvent, CompositionEvent } from 'react';
import classes from './RInputField.module.scss';

enum FieldSize {
  "small" = "210px",
  "large" = "450px",
}

type RInputFieldProps<T extends HTMLInputElement | HTMLTextAreaElement = HTMLInputElement> = {
  type?: string;
  label?: string;
  defaultValue?: any;
  required?: boolean;
  register?: any;
  error?: any;
  errorMessage?: string;
  name: string;
  instruction?: string;
  size?: 'small' | 'large';
  hidden?: boolean;
  textArea?: boolean;
  placeholder?: string;
  maxLength?: number;
  value?: string;
  privacy?: 'allow' | 'mask' | 'hidden' | 'mask-user-input';
  onChange?: (e: ChangeEvent<T>) => void;
  onCompose?: (e: CompositionEvent<HTMLInputElement>) => void;
};

const RInputField = <T extends HTMLInputElement | HTMLTextAreaElement = HTMLInputElement>({
  type,
  label,
  defaultValue,
  value,
  name,
  onChange,
  onCompose,
  errorMessage,
  register = {},
  error,
  textArea = false,
  hidden = false,
  required = false,
  size = "large",
  placeholder,
  maxLength,
  instruction,
  privacy = undefined,
}: RInputFieldProps<T>) => {

  const handleChange = (e: ChangeEvent<T>) => {
    if (register?.onChange) register.onChange(e);
    if (onChange) onChange(e);
  };

  const handleRef = (el: any) => {
    if (register?.ref) {
      if (typeof register.ref === 'function') register.ref(el);
      else if (register.ref.current !== undefined) register.ref.current = el;
    }
  };

  return (
    <>
      {label && (
        <label className={classes.label}>
          {label}
          {required && "*"}
          {instruction && <small> ({instruction})</small>}
        </label>
      )}

      {textArea ? (
        <textarea
          style={{ width: FieldSize[size] }}
          defaultValue={defaultValue}
          {...register}
          ref={handleRef}
          className={`form-control ${error && 'is-invalid'} ${classes.field}`}
          rows={4}
          maxLength={maxLength}
          placeholder={placeholder}
          data-dd-privacy={privacy}
          onChange={handleChange as any}
        />
      ) : (
        <input
          style={{ width: FieldSize[size] }}
          defaultValue={defaultValue}
          type={type}
          hidden={hidden}
          value={value}
          {...register}
          ref={handleRef}
          className={`form-control ${error && 'is-invalid'} ${classes.field}`}
          maxLength={maxLength}
          placeholder={placeholder}
          data-dd-privacy={privacy}
          onChange={handleChange as any}
          onBeforeInput={onCompose}
        />
      )}

      {error && (
        (error.type === "required") ? (
          <div className="invalid-feedback">
            {errorMessage ? errorMessage : `${name} can't be blank.`}
          </div>
        ) : (
          <div className="invalid-feedback">
            {errorMessage ? errorMessage : `Please provide a valid ${name}.`}
          </div>
        )
      )}
    </>
  );
};

export default RInputField;
